import { CouponData } from 'modules/transactions/new-sales-entry/components/sections/CouponModal';
import { SalesEntryItem, PaymentEntryItem, useSalesEntry } from '../contexts/SalesEntryContext';


// Types for calculation inputs and results
export interface CalculationInput {
  items: SalesEntryItem[];
  customer?: {
    id: string;
    creditAmount: number;
    rewardPoints: number;
    membershipTier?: 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM';
  };
  discounts?: {
    consumerOfferTotal?: any,
    couponDiscount: number;
    storeDiscount: number;
    membershipDiscount: number;
    coupon: CouponData | null
  };
  oldGold?: {
    weight: number;
    purity: number;
    currentRate: number;
    amount?: number; // Direct amount override if provided
  };
  exchange?: {
    itemValue: number;
  };
  redemption?: {
    pointsToRedeem: number;
  };
  payments?: PaymentEntryItem[];
}

export interface CalculationResult {
  summary: {
    ornamentAmt: number;
    makingCharges: number;
    subAmt: number;
    consumerOffer: number;
    storeOffer: number;
    discountVoucher: number;
    totalOffer: number;
    taxableAmount: number;
    cgst: number;
    sgst: number;
    totalTax: number;
    totalAmt: number;
    totalDueAmt: number;
  };
  voucherDetails: {
    oldGoldAmt: number;
    exchangeAmt: number;
    voucherAmt: number;
  };
  customerDetails: {
    creditAmt: number;
    rewardPointsEarned: number;
    rewardPointsRedeemed: number;
    rewardPointsBalance: number;
  };
  paymentBreakdown: {
    cashAmount: number;
    cardAmount: number;
    upiAmount: number;
    creditUsed: number;
    pointsRedeemed: number;
    oldGoldValue: number;
    exchangeValue: number;
    pendingAmount: number;
  };
}

export class SalesCalculationService {

  /**
   * Calculate item-level amounts including making charges
   */
  private calculateItemAmounts(items: SalesEntryItem[]): {
    ornamentAmt: number;
    makingCharges: number;
    totalItems: number;
  } {
    let ornamentAmt = 0;
    let makingCharges = 0;
    let totalItems = items.length;

    items.forEach(item => {
      // Base ornament amount (MRP * quantity)
      const itemAmount = (item.mrp || 0) * (item.pcs || 1);
      ornamentAmt += itemAmount;

      // Calculate making charges based on item type
      let makingChargeRate = 0

      // If item has diamonds, use diamond making charge rate
      if (item.diaQty && item.diaQty > 0) {
        makingChargeRate = 0
      }

      // Making charges on net weight * current gold rate
      const goldValue = 0;
      makingCharges += goldValue * makingChargeRate;
    });

    return { ornamentAmt, makingCharges, totalItems };
  }

  /**
   * Calculate discount amounts
   */
  private calculateDiscounts(
    discounts?: CalculationInput['discounts']
  ): {
    consumerOffer: number;
    storeOffer: number;
    discountVoucher: number;
    totalOffer: number;
  } {
    const couponDiscount = discounts?.couponDiscount || 0;

    const storeDiscount = discounts?.storeDiscount || 0;

    const membershipDiscount = discounts?.consumerOfferTotal || 0;

    return {
      consumerOffer: membershipDiscount,
      storeOffer: storeDiscount,
      discountVoucher: couponDiscount,
      totalOffer: couponDiscount + storeDiscount,
    };
  }

  /**
   * Calculate tax amounts
   */
  private calculateTaxes(taxableAmount: number): {
    cgst: number;
    sgst: number;
    totalTax: number;
  } {
    const cgst = taxableAmount * 0.015; // 1.5% CGST
    const sgst = taxableAmount * 0.015; // 1.5% SGST

    const truncateTo2Decimals = (num: number): number => {
      return Math.floor(num * 100) / 100;
    };

    return {
      cgst: truncateTo2Decimals(cgst),
      sgst: truncateTo2Decimals(sgst),
      totalTax: truncateTo2Decimals(cgst + sgst),
    };
  }

  /**
   * Calculate old gold value
   */
  private calculateOldGoldValue(oldGold?: CalculationInput['oldGold']): number {
    if (!oldGold) return 0;

    // If direct amount is provided, use it (for manual old gold entries)
    if (oldGold.amount !== undefined) {
      return oldGold.amount;
    }

    // Otherwise calculate from weight, purity, and rate
    const purityMultiplier = oldGold.purity / 24; // Convert purity to decimal
    return oldGold.weight * oldGold.currentRate * purityMultiplier;
  }

  /**
   * Calculate reward points
   */
  private calculateRewardPoints(
    totalAmount: number,
    customer?: CalculationInput['customer'],
    redemption?: CalculationInput['redemption']
  ): {
    rewardPointsEarned: number;
    rewardPointsRedeemed: number;
    rewardPointsBalance: number;
  } {
    const baseEarningRate = 0;

    // Tier-based earning multiplier
    const tierMultiplier = {
      BRONZE: 1.0,
      SILVER: 1.2,
      GOLD: 1.5,
      PLATINUM: 2.0,
    };

    const multiplier = customer?.membershipTier
      ? tierMultiplier[customer.membershipTier]
      : 1.0;

    const rewardPointsEarned = Math.floor(totalAmount * baseEarningRate * multiplier);
    const rewardPointsRedeemed = redemption?.pointsToRedeem || 0;
    const currentPoints = customer?.rewardPoints || 0;
    const rewardPointsBalance = currentPoints + rewardPointsEarned - rewardPointsRedeemed;

    return {
      rewardPointsEarned,
      rewardPointsRedeemed,
      rewardPointsBalance,
    };
  }

  /**
   * Main calculation method
   */
  public calculateSalesEntry(input: CalculationInput): CalculationResult {
    // Step 1: Calculate item amounts
    const { ornamentAmt, makingCharges } = this.calculateItemAmounts(input.items);
    const subAmt = ornamentAmt + makingCharges;

    // Step 2: Calculate discounts
    const discountAmounts = this.calculateDiscounts(input.discounts);

    if (input.discounts?.coupon?.withGst) {
      discountAmounts.totalOffer -= (input.discounts?.coupon?.couponPrice ?? 0);
    }

    // Step 3: Calculate taxable amount (subAmt minus discounts)
    let taxableAmount = subAmt - (discountAmounts.totalOffer + discountAmounts.consumerOffer);
    
    // Prevent negative taxableAmount
    // 23/8/25
    if (taxableAmount < 0) {
      taxableAmount = 0;
    } 
    //
    
    // Step 4: Calculate taxes on taxable amount (1.5% CGST + 1.5% SGST = 3% total)
    const taxAmounts = this.calculateTaxes(taxableAmount);
   
    // Step 5: Calculate total amount = subAmt + taxes (discounts are already subtracted from taxable amount)
    // This follows the formula: subAmt - offers + taxes = taxableAmount + taxes
    let totalAmt = taxableAmount + taxAmounts.totalTax;

    if (input.discounts?.coupon?.withGst) {
      // discountAmounts.totalOffer += (input.discounts?.coupon?.couponPrice ?? 0);
    }

    // Step 6: Calculate old gold and exchange values
    const oldGoldAmt = this.calculateOldGoldValue(input.oldGold);
    const exchangeAmt = input.exchange?.itemValue || 0;
    const voucherAmt = exchangeAmt;

    // Step 7: Calculate reward points
    const rewardPointsCalc = this.calculateRewardPoints(
      totalAmt,
      input.customer,
      input.redemption
    );

    // Step 8: Calculate final due amount
    const rewardPointsRedemptionValue = rewardPointsCalc.rewardPointsRedeemed * 1;
    const creditUsed = Math.min(input.customer?.creditAmount || 0, totalAmt);

    let totalDueAmtBeforePayments = totalAmt - voucherAmt - oldGoldAmt - rewardPointsRedemptionValue - creditUsed;

    // if (input.discounts?.coupon?.withGst === false) {
    //   totalDueAmtBeforePayments = (input.discounts?.coupon?.couponPrice ?? 0);
    // }

    // Step 9: Payment breakdown and final due amount
    const totalPaymentAmount = input.payments?.reduce((total, payment) => total + payment.amount, 0) || 0;
    const totalDueAmt = totalDueAmtBeforePayments - totalPaymentAmount;
    const pendingAmount = totalDueAmt; // Same as totalDueAmt after payments

    return {
      summary: {
        ornamentAmt: Math.round(ornamentAmt * 100) / 100,
        makingCharges: Math.round(makingCharges * 100) / 100,
        subAmt: Math.round(subAmt * 100) / 100,
        consumerOffer: Math.round(discountAmounts.consumerOffer * 100) / 100,
        storeOffer: Math.round(discountAmounts.storeOffer * 100) / 100,
        discountVoucher: Math.round(discountAmounts.discountVoucher * 100) / 100,
        totalOffer: Math.round(discountAmounts.totalOffer * 100) / 100,
        taxableAmount: Math.round(taxableAmount * 100) / 100,
        cgst: taxAmounts.cgst,
        sgst: taxAmounts.sgst,
        totalTax: taxAmounts.totalTax,
        totalAmt: Math.round(totalAmt * 100) / 100,
        totalDueAmt: Math.round(totalDueAmt * 100) / 100,
      },
      voucherDetails: {
        oldGoldAmt: Math.round(oldGoldAmt * 100) / 100,
        exchangeAmt: Math.round(exchangeAmt * 100) / 100,
        voucherAmt: Math.round(voucherAmt * 100) / 100,
      },
      customerDetails: {
        creditAmt: input.customer?.creditAmount || 0,
        rewardPointsEarned: rewardPointsCalc.rewardPointsEarned,
        rewardPointsRedeemed: rewardPointsCalc.rewardPointsRedeemed,
        rewardPointsBalance: rewardPointsCalc.rewardPointsBalance,
      },
      paymentBreakdown: {
        cashAmount: input.payments?.filter(p => p.method.toLowerCase() === 'cash').reduce((sum, p) => sum + p.amount, 0) || 0,
        cardAmount: input.payments?.filter(p => p.method.toLowerCase() === 'card').reduce((sum, p) => sum + p.amount, 0) || 0,
        upiAmount: input.payments?.filter(p => p.method.toLowerCase() === 'upi').reduce((sum, p) => sum + p.amount, 0) || 0,
        creditUsed: Math.round(creditUsed * 100) / 100,
        pointsRedeemed: Math.round(rewardPointsRedemptionValue * 100) / 100,
        oldGoldValue: Math.round(oldGoldAmt * 100) / 100,
        exchangeValue: Math.round(exchangeAmt * 100) / 100,
        pendingAmount: Math.round(pendingAmount * 100) / 100,
      },
    };
  }

  /**
   * Validate calculation inputs
   */
  public validateCalculationInput(input: CalculationInput): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Validate items
    if (!input.items || input.items.length === 0) {
      errors.push('At least one item is required');
    }

    input.items.forEach((item, index) => {
      if (!item.mrp || item.mrp <= 0) {
        errors.push(`Item ${index + 1}: MRP is required and must be positive`);
      }
      if (!item.netWt || item.netWt <= 0) {
        errors.push(`Item ${index + 1}: Net weight is required and must be positive`);
      }
    });

    // Validate old gold if provided
    if (input.oldGold) {
      if (input.oldGold.weight <= 0) {
        errors.push('Old gold weight must be positive');
      }
      if (input.oldGold.purity <= 0 || input.oldGold.purity > 24) {
        errors.push('Old gold purity must be between 1 and 24');
      }
      if (input.oldGold.currentRate <= 0) {
        errors.push('Gold rate must be positive');
      }
    }

    // Validate redemption points
    if (input.redemption && input.customer) {
      if (input.redemption.pointsToRedeem > input.customer.rewardPoints) {
        errors.push('Cannot redeem more points than available');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

// Singleton instance
export const calculationService = new SalesCalculationService();